import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { AlertService } from './api/alerts-api.service';
import { AccountsService } from '@app/services/app-services/accounts.service';
import { FeatureState } from '@app/models';
import { IAppData } from '@app/models';
import { AppDataService } from './store/app-data.service';
import { FallbackIoTDBAccessFrequency } from '@app/constants/fallbacks-constants';

@Injectable({
    providedIn: 'root',
})
export class FeaturesStatesService implements OnDestroy {
    private _currentAccountId: string;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _subscriberFeatureStates: BehaviorSubject<FeatureState[] | null> = new BehaviorSubject(null);
    private _subscriberFeatureAlerts: BehaviorSubject<FeatureState[] | null> = new BehaviorSubject(null);
    dataRequestInterval: any;
    private _appData: IAppData;
    private _currentFeaureStates: FeatureState[] = [];

    constructor(private _alertService: AlertService, private _accountsService: AccountsService, private _appDataService: AppDataService) {
        this._appDataService.appData$.pipe(takeUntil(this._unsubscribeAll)).subscribe((data) => (this._appData = data));
        this._accountsService
            .getAccountId()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((account: string) => {
                this._currentAccountId = account;
                if (!!account) {
                    this._getFeatureStates();
                }
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for features states
     */
    get featuresSatates$(): Observable<FeatureState[]> {
        return this._subscriberFeatureStates.asObservable();
    }
    /**
     * Getter for features states
     */
    get featuesAlerts$(): Observable<FeatureState[]> {
        return this._subscriberFeatureAlerts.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        clearInterval(this.dataRequestInterval);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * start the cyclic alert call from api every one minute
     */
    private _activateFeatureStatesReading(): void {
        this._getFeatureStates();
        clearInterval(this.dataRequestInterval);
        this.dataRequestInterval = setInterval(() => {
            this._getFeatureStates();
        }, this._appData.GeneralData.IoTDBAccessFrequency ?? FallbackIoTDBAccessFrequency);
    }

    /**
     * gets the Feature States from API
     */
    private _getFeatureStates(): void {
        this._alertService.getSubscriberFeatureStatesList(this._currentAccountId).subscribe((SubscriberFeatureStates: FeatureState[]) => {
            this._currentFeaureStates = SubscriberFeatureStates;
            this._subscriberFeatureStates.next(SubscriberFeatureStates);
            this._subscriberFeatureAlerts.next(SubscriberFeatureStates.filter((f) => f.IsAlert));
        });
    }

    /**
     * updates the Feature States
     * @param sfs 
     */
    public updateFeatureStates(receivedFeatureStates:  FeatureState[]) {
        if (receivedFeatureStates) {
            this._currentFeaureStates.forEach(x => x.IsReceivedUpdate = false);
            receivedFeatureStates.forEach(x => x.IsReceivedUpdate = true);
            this._currentFeaureStates = this._currentFeaureStates.map(current => {
                const changedState = receivedFeatureStates.find(received => (received.SubscriberId == current.SubscriberId) && (received.Feature == current.Feature));
                return !!changedState ? changedState : current;
            });
            this._subscriberFeatureStates.next(this._currentFeaureStates);            
            this._subscriberFeatureAlerts.next(this._currentFeaureStates.filter((f) => f.IsAlert));            
        }
    }

    /**
     * gets the Feature States from API from outside the service
     */
    public getFeatureStates(): void {
        this._getFeatureStates();
    }
}
